import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { salesReportQuery } from '@/api/goods'
import {
  clientBaseQuery,
  passHoldersQuery,
  passSalesQuery,
  servicePaymentsQuery,
  visitorsQuery,
  type PassSales,
} from '@/api/overview'
import { branchesQuery } from '@/api/queries'
import { useCompanyCaller } from '@/app/company'
import { NothingForRole } from '@/app/nothing-for-role'
import { visibleBranches } from '@/domain/client-abilities'
import {
  canSeeOverview,
  DEFAULT_PERIOD,
  goodsFigures,
  isBlank,
  isOverviewPeriod,
  OVERVIEW_PERIODS,
  passFigures,
  readOf,
  serviceFigures,
  STREAMS,
  TOP_PLANS,
  totalOf,
  unassignedOf,
  type OverviewPeriodKind,
  type Read,
  type StreamFigures,
  type StreamKey,
  type Streams,
} from '@/domain/overview'
import { formatMoney, formatMoneyColumn } from '@/domain/pass-rules'
import { dayText, periodRange, periodText, reportZoneOf } from '@/domain/periods'
import { clubShelf, EVERY_SHELF } from '@/domain/shelves'
import { t } from '@/i18n'
import { cn } from '@/lib/cn'
import { withKey } from '@/lib/search'
import { DataList, type DataColumn } from '@/ui/data-list'
import { InlineBanner } from '@/ui/inline-banner'
import { Page, PageHeader } from '@/ui/page'
import { SectionHeader } from '@/ui/section-header'
import { Select } from '@/ui/select'
import { LoadingBlock } from '@/ui/spinner'
import { StatTile, StatTiles } from '@/ui/stat-tile'
import { StateFilter } from '@/ui/state-filter'

/// The period and the club, in the URL — a reload, a link and «Назад» keep
/// both. «Цей місяць» and «Усі клуби» are the defaults and stay out of it.
type OverviewSearch = { period?: OverviewPeriodKind; club?: string }

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export const Route = createFileRoute('/_app/c/$orgId/overview')({
  validateSearch: (raw: Record<string, unknown>): OverviewSearch => ({
    ...(isOverviewPeriod(raw['period']) && raw['period'] !== DEFAULT_PERIOD ? { period: raw['period'] } : {}),
    ...(typeof raw['club'] === 'string' && UUID.test(raw['club']) ? { club: raw['club'] } : {}),
  }),
  component: Overview,
})

const o = () => t.overview

const counted = new Intl.NumberFormat('uk-UA')
/// «1 248» — a count with the thousands apart, as money is.
const count = (n: number): string => counted.format(n)

/// A figure, or «—» in its place (R13).
const shown = <T,>(read: Read<T>, format: (value: T) => string): string => (read.kind === 'value' ? format(read.value) : '—')

/// The note of a tile: what the figure is made of, then its definition — a
/// hint, quieter (specs/reports.md §8.1).
const Note = ({ figure, hint }: { figure?: ReactNode; hint: string }) => (
  <>
    {figure ? <span className="block">{figure}</span> : null}
    <span className="block text-muted">{hint}</span>
  </>
)

/// «Огляд»: the owner's first numbers — how many clients the club has, how
/// many of them hold a valid pass, who came; what the passes, the services
/// and the goods brought, how many sales and the average one. Each figure is
/// its own service's answer (specs/reports.md §8); one that did not come is
/// «—» with the reason, and the totals say what they leave out (R13).
function Overview() {
  const { orgId } = Route.useParams()
  const { period = DEFAULT_PERIOD, club } = Route.useSearch()
  const navigate = Route.useNavigate()
  const { access, caller } = useCompanyCaller(orgId)
  const see = canSeeOverview(caller)
  const branches = useQuery({ ...branchesQuery(orgId), enabled: see })
  const clubs = visibleBranches(access, branches.data ?? [])
  // A link to a club the caller no longer reaches reads as every club.
  const branchId = club !== undefined && clubs.some((c) => c.id === club) ? club : null
  // Days on the CLUB's calendar — the chosen club's zone, else the first
  // one's — and their boundaries, not «now»: the query keys hold still all
  // day. Asked once the clubs are known, so a club in another zone is not
  // first asked about Kyiv's day.
  const span = periodRange(period, reportZoneOf(clubs, branchId))
  const asked = { from: span.from, to: span.to, branchId }
  const ready = see && !branches.isPending
  // Another period or club keeps the figures on screen, dimmed, until the
  // new ones arrive — not a spinner on every switch.
  const options = { enabled: ready, placeholderData: keepPreviousData } as const
  const passes = useQuery({ ...passSalesQuery(orgId, asked), ...options })
  const holders = useQuery({ ...passHoldersQuery(orgId, span.lastDay, branchId), ...options })
  const services = useQuery({ ...servicePaymentsQuery(orgId, asked), ...options })
  const goods = useQuery({ ...salesReportQuery(orgId, span.from, span.to, branchId ? clubShelf(branchId) : EVERY_SHELF), ...options })
  const base = useQuery({ ...clientBaseQuery(orgId, asked), ...options })
  const visitors = useQuery({ ...visitorsQuery(orgId, asked), ...options })
  const queries = [passes, holders, services, goods, base, visitors]

  if (!see) return <NothingForRole />

  const streams: Streams = {
    passes: mapRead(readOf(passes), passFigures),
    services: mapRead(readOf(services), serviceFigures),
    goods: mapRead(readOf(goods), goodsFigures),
  }
  const total = totalOf(streams)
  const noMoney = total.missing.length === STREAMS.length
  const people = { base: readOf(base), holders: readOf(holders), visitors: readOf(visitors) }
  const unassigned = branchId ? unassignedOf(passes.data?.unassigned, services.data?.unassigned) : null
  const blank = isBlank({
    base: people.base.kind === 'value' ? people.base.value.total : null,
    visitors: people.visitors.kind === 'value' ? people.visitors.value.visitors : null,
    total,
  })
  const retry = () => queries.filter((query) => query.isError).forEach((query) => void query.refetch())

  return (
    <Page wide>
      <PageHeader title={o().title} subtitle={o().subtitle} />

      <div className="mb-6 flex flex-col gap-2">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <StateFilter
            label={o().period}
            value={period}
            onChange={(next) =>
              void navigate({ search: (prev) => withKey(prev, 'period', next === DEFAULT_PERIOD ? undefined : next), replace: true })
            }
            options={OVERVIEW_PERIODS.map((p) => ({ value: p, label: o().periods[p] ?? p, count: undefined }))}
          />
          {/* One club is no choice: nothing to pick. */}
          {clubs.length > 1 ? (
            <Select
              variant="toolbar"
              ariaLabel={o().club}
              value={branchId ?? ''}
              onChange={(next) => void navigate({ search: (prev) => withKey(prev, 'club', next || undefined), replace: true })}
              options={[{ value: '', label: o().allClubs }, ...clubs.map((c) => ({ value: c.id, label: c.name }))]}
            />
          ) : null}
        </div>
        {/* What «Цей місяць» is today, on the club's calendar. */}
        <p className="px-1 text-caption text-text-2 tabular">{periodText(span.firstDay, span.lastDay)}</p>
      </div>

      {!ready || queries.some((query) => query.isPending) ? (
        <LoadingBlock label={t.app.loading} />
      ) : (
        <div
          className={cn(
            'flex flex-col gap-8 transition-opacity',
            queries.some((query) => query.isPlaceholderData) && 'opacity-60',
          )}
        >
          {blank ? <InlineBanner kind="empty" message={o().blank} /> : null}

          <section className="flex flex-col gap-2">
            <SectionHeader as="h2" title={o().people} inset />
            <StatTiles className="grid-cols-2 sm:grid-cols-3">
              <StatTile
                compact
                className="col-span-2 sm:col-span-1"
                label={o().base}
                value={shown(people.base, (b) => count(b.total))}
                note={
                  <Note
                    figure={people.base.kind === 'value' ? o().added(people.base.value.added) : gapText(people.base)}
                    hint={o().baseHint}
                  />
                }
              />
              <StatTile
                compact
                label={o().members}
                value={shown(people.holders, (h) => count(h.members))}
                note={
                  <Note
                    figure={
                      people.holders.kind === 'value'
                        ? [o().membersOn(dayText(span.lastDay)), people.holders.value.frozen > 0 ? o().paused(people.holders.value.frozen) : null]
                            .filter(Boolean)
                            .join(' · ')
                        : gapText(people.holders)
                    }
                    hint={o().membersHint}
                  />
                }
              />
              <StatTile
                compact
                label={o().visitors}
                value={shown(people.visitors, (v) => count(v.visitors))}
                note={
                  <Note
                    figure={
                      people.visitors.kind === 'value'
                        ? o().visits(count(people.visitors.value.visits), people.visitors.value.visits)
                        : gapText(people.visitors)
                    }
                    hint={o().visitorsHint}
                  />
                }
              />
            </StatTiles>
          </section>

          <section className="flex flex-col gap-2">
            <SectionHeader as="h2" title={o().money} inset />
            <StatTiles className="grid-cols-2 sm:grid-cols-3">
              {/* With no stream read at all there is no total — «—», not 0 ₴ (R13). */}
              <StatTile
                compact
                className="col-span-2 sm:col-span-1"
                label={o().revenue}
                value={noMoney ? '—' : formatMoney(total.netMinor)}
                note={
                  <Note
                    figure={total.refundedMinor > 0 ? o().refunded(formatMoney(total.refundedMinor)) : undefined}
                    hint={o().revenueHint}
                  />
                }
              />
              <StatTile compact label={o().sales} value={noMoney ? '—' : count(total.sales)} note={<Note hint={o().salesHint} />} />
              <StatTile
                compact
                label={o().average}
                value={total.averageMinor === null ? '—' : formatMoney(total.averageMinor)}
                note={<Note hint={o().averageHint} />}
              />
            </StatTiles>
            {total.missing.length > 0 ? (
              <InlineBanner
                kind="stale"
                message={o().missing(total.missing.map((key) => o().missingStream(o().streams[key] ?? key, gapText(streams[key]) ?? '')).join('; '))}
                action={{ label: t.app.retry, onClick: retry }}
              />
            ) : null}
            {unassigned ? (
              <InlineBanner kind="info" message={o().unassigned(unassigned.sales, formatMoney(unassigned.revenueMinor))} />
            ) : null}
          </section>

          <section className="flex flex-col gap-2">
            <SectionHeader as="h2" title={o().byStream} inset />
            <DataList
              label={o().byStream}
              columns={STREAM_COLUMNS}
              rows={STREAMS.map((key) => ({ key, read: streams[key] }))}
              rowKey={(row) => row.key}
            />
            <p className="px-4 text-caption text-pretty text-muted">{o().streamsFootnote}</p>
          </section>

          {/* The passes' own lines — only when there is something to split. */}
          {passes.data && (passes.data.sales > 0 || passes.data.free > 0 || passes.data.refunds.count > 0) ? (
            <PassesSection report={passes.data} />
          ) : null}
        </div>
      )}
    </Page>
  )
}

const mapRead = <A, B>(read: Read<A>, map: (value: A) => B): Read<B> =>
  read.kind === 'value' ? { kind: 'value', value: map(read.value) } : read

/// Why a figure is «—», in words; nothing for a figure that is there.
const gapText = (read: Read<unknown>): string | undefined => (read.kind === 'gap' ? o().gaps[read.gap] : undefined)

type StreamRow = Readonly<{ key: StreamKey; read: Read<StreamFigures> }>

/// A money cell of a stream: the figure, or «—» when the stream did not come.
const figure = (row: StreamRow, format: (figures: StreamFigures) => ReactNode): ReactNode =>
  row.read.kind === 'value' ? format(row.read.value) : <span className="text-muted">—</span>

// On a phone: the stream, «46 продажів · чек 1 269,57 ₴» under it, the
// revenue on the right.
const STREAM_COLUMNS: readonly DataColumn<StreamRow>[] = [
  {
    key: 'stream',
    header: t.overview.columns.stream,
    width: 'minmax(0,1.6fr)',
    cell: (row) => (
      <span className="flex min-w-0 flex-col">
        <span className="truncate font-medium text-text">{o().streams[row.key]}</span>
        <span className="truncate text-caption text-text-2">{gapText(row.read) ?? o().streamHints[row.key]}</span>
      </span>
    ),
  },
  {
    key: 'revenue',
    header: t.overview.columns.revenue,
    width: 'minmax(120px,1fr)',
    numeric: true,
    mobile: 'trailing',
    cell: (row) => figure(row, (f) => formatMoneyColumn(f.netMinor)),
  },
  {
    key: 'sales',
    header: t.overview.columns.sales,
    width: 'minmax(80px,0.7fr)',
    numeric: true,
    mobile: 'subtitle',
    cell: (row) =>
      figure(row, (f) => (
        <>
          <span className="sm:hidden">{o().salesShort(f.sales)}</span>
          <span className="hidden sm:inline">{count(f.sales)}</span>
        </>
      )),
  },
  {
    key: 'average',
    header: t.overview.columns.average,
    width: 'minmax(120px,1fr)',
    numeric: true,
    mobile: 'subtitle',
    cell: (row) =>
      figure(row, (f) => (
        <span className={f.averageMinor === null ? 'text-muted' : undefined}>
          <span className="sm:hidden">{o().averageShort} </span>
          {f.averageMinor === null ? '—' : formatMoneyColumn(f.averageMinor)}
        </span>
      )),
  },
  // Money given back in the period; goods have no returns yet («—»).
  {
    key: 'refunds',
    header: t.overview.columns.refunds,
    width: 'minmax(110px,0.9fr)',
    numeric: true,
    hideBelow: 'md',
    mobile: 'hidden',
    cell: (row) =>
      row.key === 'goods' ? (
        <span className="text-muted">—</span>
      ) : (
        figure(row, (f) =>
          f.refundedMinor > 0 ? `−${formatMoneyColumn(f.refundedMinor)}` : <span className="text-muted">{formatMoneyColumn(0)}</span>,
        )
      ),
  },
]

type PlanRow = PassSales['plans'][number]

const PLAN_COLUMNS: readonly DataColumn<PlanRow>[] = [
  { key: 'name', header: t.overview.planColumns.name, width: 'minmax(0,2fr)', cell: (row) => row.name },
  {
    key: 'sales',
    header: t.overview.planColumns.sales,
    width: 'minmax(80px,0.7fr)',
    numeric: true,
    mobile: 'subtitle',
    cell: (row) => (
      <>
        <span className="sm:hidden">{o().salesShort(row.sales)}</span>
        <span className="hidden sm:inline">{count(row.sales)}</span>
      </>
    ),
  },
  {
    key: 'revenue',
    header: t.overview.planColumns.revenue,
    width: 'minmax(120px,1fr)',
    numeric: true,
    mobile: 'trailing',
    cell: (row) => formatMoneyColumn(row.revenueMinor),
  },
]

/// The passes' own lines: how the sales split between the desk and the app,
/// what the app gave back, what was given for nothing, and the best sellers.
const PassesSection = ({ report }: { report: PassSales }) => {
  const lines = [
    report.refunds.count > 0 ? o().appRefunds(report.refunds.count, formatMoney(report.refunds.amountMinor)) : null,
    report.free > 0 ? o().free(report.free) : null,
  ].filter((line): line is string => line !== null)
  const rest = report.plans.length - TOP_PLANS
  return (
    <section className="flex flex-col gap-2">
      <SectionHeader as="h2" title={o().passes} inset />
      <p className="px-4 text-caption text-text-2 tabular">
        {o().channels(report.desk.sales, formatMoney(report.desk.revenueMinor), report.app.sales, formatMoney(report.app.revenueMinor))}
      </p>
      {lines.length > 0 ? <p className="px-4 text-caption text-muted">{lines.join(' · ')}</p> : null}
      <DataList
        label={o().plans}
        columns={PLAN_COLUMNS}
        rows={report.plans.slice(0, TOP_PLANS)}
        rowKey={(row) => row.name}
        empty={o().noPlans}
      />
      {rest > 0 ? <p className="px-4 text-caption text-muted">{o().morePlans(rest)}</p> : null}
    </section>
  )
}
